/** CE SE SCRIE ÎN SOLUȚIE după editarea parcajului: doar ce s-a schimbat.
 *
 *  Un loc mutat sau rotit ÎȘI PĂSTREAZĂ obiectul din document — id-ul, felul,
 *  `props`, numărul de pe planșă — și primește doar geometria nouă; unul nou e
 *  un `ParkingSpace` întreg, cu sursa `manual` ca documentul să spună că l-a
 *  pus omul, nu solverul (același `SURSA` ca la editorul de nivel,
 *  `plan/nivelTronson.ts`); unul șters e un id în `sterge`.
 *
 *  Totul aici e în cadrul DESENULUI; drumul înapoi în cadrul documentului îl
 *  face `nivelTronson.inCadrulDocumentului`, în `useEditorParcaj`, ca la
 *  nivel — același cod pentru `deg` și puncte, ca o cheie uitată să nu scrie un
 *  loc rotit un sfert de tură.
 */
import type { CanonicalObject } from "../canonical";
import type { Alee, Loc } from "./model";

export const SURSA = { kind: "manual" as const, engine: "editor" };

export type Diferenta = { schimbate: Loc[]; noi: Loc[]; sterse: string[] };

/** Unghiurile se compară MODULO 360: −2,85° și 357,15° sunt același loc, iar
 *  lipirea la unghi normalizează în [0, 360). */
const acelasiUnghi = (a: number, b: number) =>
  Math.abs(((a - b) % 360 + 540) % 360 - 180) < 1e-6;
const acelasi = (a: Loc, b: Loc) =>
  Math.abs(a.cx - b.cx) < 1e-6 && Math.abs(a.cy - b.cy) < 1e-6
  && acelasiUnghi(a.deg, b.deg) && Math.abs(a.w - b.w) < 1e-6
  && Math.abs(a.d - b.d) < 1e-6 && a.kind === b.kind;

/** Ce diferă între lista inițială și cea finală. */
export function diferenta(initial: readonly Loc[], final: readonly Loc[]): Diferenta {
  const fost = new Map(initial.map((l) => [l.id, l]));
  const este = new Set(final.map((l) => l.id));
  const schimbate: Loc[] = [], noi: Loc[] = [];
  for (const l of final) {
    const f = fost.get(l.id);
    if (!f) noi.push(l);
    else if (!acelasi(f, l)) schimbate.push(l);
  }
  const sterse = initial.filter((l) => !este.has(l.id)).map((l) => l.id);
  return { schimbate, noi, sterse };
}

/** Numerele curățate la milimetru — nu zgomotul virgulei mobile al unei
 *  translații. */
const mm = (x: number) => Math.round(x * 1000) / 1000;

/** Locurile ca obiecte canonice (în cadrul desenului): cele cu obiect în
 *  `originale` îl păstrează și primesc geometria nouă; restul sunt noi. */
export function caObiecte(locuri: readonly Loc[],
                          originale: ReadonlyMap<string, CanonicalObject>,
                          storeyId: string): CanonicalObject[] {
  return locuri.map((l) => {
    const geo = { cx: mm(l.cx), cy: mm(l.cy), deg: mm(((l.deg % 360) + 360) % 360),
                  w: mm(l.w), h: mm(l.d) };
    const orig = originale.get(l.id);
    if (orig) {
      return { ...orig, geometry: { ...(orig.geometry ?? {}), ...geo },
               display: { ...(orig.display ?? {}), n: l.n } };
    }
    return {
      id: l.id, type: "ParkingSpace",
      properties: { kind: l.kind, storey: storeyId, form: l.form ?? "perp" },
      geometry: geo, display: { n: l.n }, source: SURSA,
    } as CanonicalObject;
  });
}

/** Aleile cu axa schimbată — doar ele se scriu. */
export function diferentaAlei(initial: readonly Alee[], final: readonly Alee[]): Alee[] {
  const fost = new Map(initial.map((a) => [a.eid, a]));
  return final.filter((a) => {
    const f = fost.get(a.eid);
    return !f || Math.abs(f.x1 - a.x1) > 1e-6 || Math.abs(f.y1 - a.y1) > 1e-6
      || Math.abs(f.x2 - a.x2) > 1e-6 || Math.abs(f.y2 - a.y2) > 1e-6;
  });
}

/** Aleile ȘTERSE: cele de la deschidere care nu mai sunt pe masă. */
export function aleiSterse(initial: readonly Alee[], final: readonly Alee[]): Alee[] {
  const este = new Set(final.map((a) => a.eid));
  return initial.filter((a) => !este.has(a.eid));
}

/** Aleile ca obiecte canonice (în cadrul desenului): obiectul `Lane` din
 *  document, cu axa nouă. O alee fără obiect în document nu se poate scrie —
 *  editorul nu creează alei, doar le lungește. */
export function aleiCaObiecte(alei: readonly Alee[],
                              originale: ReadonlyMap<string, CanonicalObject>): CanonicalObject[] {
  const out: CanonicalObject[] = [];
  for (const a of alei) {
    const orig = originale.get(a.eid);
    if (!orig) continue;
    out.push({ ...orig, geometry: { ...(orig.geometry ?? {}),
                                    axis: [[mm(a.x1), mm(a.y1)], [mm(a.x2), mm(a.y2)]] } });
  }
  return out;
}
